import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ScrollToTop } from '../src/components/shared/ScrollToTop';
import { Seo } from './components/Seo';
import '../src/index.css';
import { DemoLayout } from './DemoLayout';
import Home from './pages/Home.tsx';
// import Features from './pages/Features';
import FAQ from './pages/FAQ';
import GovernanceIndex from './pages/governance/Index';
import HowItWorks from './pages/governance/HowItWorks';
import MergeBylaws from './pages/governance/MergeBylaws';
import CommunityGovernancePage from './pages/governance/Community';
import MontanaGlossary from './pages/governance/MontanaGlossary';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import DemoPage from './pages/Demo.tsx';
import { MontanaPricingPage } from '../src/pages/pricing/montana-pricing';
import { PricingAssessmentWizard } from '../src/components/entities/pricing/PricingAssessmentWizard';
import { PricingAssessmentSuccess } from '../src/components/entities/pricing/PricingAssessmentSuccess';
import { SilverWelcome } from '../src/pages/onboarding/welcome/SilverWelcome';
import { GoldWelcome } from '../src/pages/onboarding/welcome/GoldWelcome';
import { ContactSalesWelcome } from '../src/pages/onboarding/welcome/ContactSalesWelcome';
import CommunityOnboardingPage from '../src/pages/CommunityOnboardingPage';
import { AssessmentWizard } from '../src/components/entities/community_subpanel-elect/customer/onboarding/AssessmentWizard';
import { SalesQualificationPage } from '../src/pages/onboarding/SalesQualificationPage';
import ScheduleDemoPage from '../src/pages/ScheduleDemo';
import SalesPage from '../src/pages/SalesPage';
import { CityStateTest } from '../src/components/test/CityStateTest';

export function DemoApp() {
  return (
    <HelmetProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToTop />
        <DemoLayout>
          <Routes>
            <Route
              index
              element={
                <Seo
                  title="Montana Community Management | ToolboxLogic"
                  path="/"
                  description="Integrated platform for Montana community governance, payments, vendor management, and group buying."
                >
                  <Home />
                </Seo>
              }
            />
            {/* <Route path="features" element={<Features />} /> */}
            <Route
              path="faq"
              element={
                <Seo
                  title="Montana Community FAQ | ToolboxLogic"
                  path="/faq"
                  description="Answers to common questions about the ToolboxLogic Montana community platform."
                >
                  <FAQ />
                </Seo>
              }
            />
            <Route
              path="demo"
              element={
                <Seo
                  title="Montana Platform Demo | ToolboxLogic"
                  path="/demo"
                  description="Explore the Montana community platform features in an interactive demo."
                >
                  <DemoPage />
                </Seo>
              }
            />
            <Route
              path="schedule-demo"
              element={
                <Seo
                  title="Schedule Demo | ToolboxLogic Montana"
                  path="/schedule-demo"
                  description="Schedule a personalized demo of the Community App platform."
                >
                  <ScheduleDemoPage />
                </Seo>
              }
            />
            <Route
              path="pricing"
              element={
                <Seo
                  title="Montana Pricing | ToolboxLogic"
                  path="/pricing"
                  description="Transparent pricing designed for Montana communities."
                >
                  <MontanaPricingPage />
                </Seo>
              }
            />
            <Route
              path="pricing/montana/406-parcels"
              element={
                <Seo
                  title="Montana Pricing – 406 Parcels | ToolboxLogic"
                  path="/pricing/montana/406-parcels"
                  description="Pricing details for a 406-parcel Montana community."
                >
                  <MontanaPricingPage />
                </Seo>
              }
            />
            <Route
              path="pricing/assessment"
              element={
                <Seo
                  title="Community Assessment | ToolboxLogic Montana"
                  path="/pricing/assessment"
                  description="Tell us about your community to get personalized pricing."
                >
                  <PricingAssessmentWizard />
                </Seo>
              }
            />
            <Route
              path="pricing/assessment/success"
              element={
                <Seo
                  title="Assessment Complete | ToolboxLogic Montana"
                  path="/pricing/assessment/success"
                  description="Your assessment is complete - schedule your demo."
                >
                  <PricingAssessmentSuccess />
                </Seo>
              }
            />
            <Route path="governance">
              {/* Primary governance experience now lives directly at /governance */}
              <Route
                index
                element={
                  <Seo
                    title="Governance for Montana Communities | ToolboxLogic"
                    path="/governance"
                    description="Digital governance tailored to Montana regulations and community needs."
                  >
                    <GovernanceIndex />
                  </Seo>
                }
              />
              {/* Legacy path kept for backward compatibility – redirect handled in component */}
              <Route
                path="community"
                element={
                  <Seo
                    title="Governance Demo | ToolboxLogic"
                    path="/governance"
                    description="Experience digital governance for your 406-member Montana community."
                  >
                    <GovernanceIndex />
                  </Seo>
                }
              />
              <Route
                path="glossary"
                element={
                  <Seo
                    title="Montana Governance Glossary | ToolboxLogic"
                    path="/governance/glossary"
                    description="Plain-English explanations of governance terms for Montana communities."
                    ogImage="/Community App - Logo.png"
                    siteName="ToolboxLogic Montana"
                    ogType="website"
                    includeWebPageLd
                  >
                    <MontanaGlossary />
                  </Seo>
                }
              />
              <Route
                path="how-it-works"
                element={
                  <Seo
                    title="How Governance Works | ToolboxLogic Montana"
                    path="/governance/how-it-works"
                    description="See how ToolboxLogic streamlines governance for Montana communities."
                  >
                    <HowItWorks />
                  </Seo>
                }
              />
              <Route
                path="merge-bylaws"
                element={
                  <Seo
                    title="Merge Bylaws | ToolboxLogic Montana"
                    path="/governance/merge-bylaws"
                    description="Tools to consolidate and manage bylaws for Montana communities."
                  >
                    <MergeBylaws />
                  </Seo>
                }
              />
            </Route>
            <Route
              path="terms"
              element={
                <Seo
                  title="Terms & Conditions | ToolboxLogic Montana"
                  path="/terms"
                  description="Terms and conditions for the ToolboxLogic Montana demo site."
                >
                  <Terms />
                </Seo>
              }
            />
            <Route
              path="privacy"
              element={
                <Seo
                  title="Privacy Policy | ToolboxLogic Montana"
                  path="/privacy"
                  description="Privacy policy for the ToolboxLogic Montana demo site."
                >
                  <Privacy />
                </Seo>
              }
            />

            {/* Onboarding routes */}
            <Route
              path="onboarding"
              element={
                <Seo
                  title="Community Onboarding | ToolboxLogic Montana"
                  path="/onboarding"
                  description="Start onboarding your Montana community with ToolboxLogic."
                >
                  <CommunityOnboardingPage />
                </Seo>
              }
            />
            <Route
              path="onboarding/assessment"
              element={
                <Seo
                  title="Onboarding Assessment | ToolboxLogic Montana"
                  path="/onboarding/assessment"
                  description="Assessment wizard to tailor the platform to your community."
                >
                  <AssessmentWizard />
                </Seo>
              }
            />
            <Route
              path="onboarding/sales-qualification"
              element={
                <Seo
                  title="Sales Qualification | ToolboxLogic Montana"
                  path="/onboarding/sales-qualification"
                  description="Help our team understand your Montana community's needs."
                >
                  <SalesQualificationPage />
                </Seo>
              }
            />
            <Route
              path="onboarding/welcome/silver"
              element={
                <Seo
                  title="Welcome – Silver Plan | ToolboxLogic Montana"
                  path="/onboarding/welcome/silver"
                  description="Welcome to the Silver plan."
                >
                  <SilverWelcome />
                </Seo>
              }
            />
            <Route
              path="onboarding/welcome/gold"
              element={
                <Seo
                  title="Welcome – Gold Plan | ToolboxLogic Montana"
                  path="/onboarding/welcome/gold"
                  description="Welcome to the Gold plan."
                >
                  <GoldWelcome />
                </Seo>
              }
            />
            <Route
              path="onboarding/welcome/contact-sales"
              element={
                <Seo
                  title="Welcome – Contact Sales | ToolboxLogic Montana"
                  path="/onboarding/welcome/contact-sales"
                  description="Get in touch with our sales team."
                >
                  <ContactSalesWelcome />
                </Seo>
              }
            />

            {/* Sales route */}
            <Route
              path="sales"
              element={
                <Seo
                  title="Contact Sales | ToolboxLogic Montana"
                  path="/sales"
                  description="Talk with our Montana-focused team about your community needs."
                >
                  <SalesPage />
                </Seo>
              }
            />

            {/* Test route for city/state component */}
            <Route
              path="test/city-state"
              element={
                <Seo
                  title="City/State Test | ToolboxLogic Montana"
                  path="/test/city-state"
                  description="Test page for city/state component."
                >
                  <CityStateTest />
                </Seo>
              }
            />

            <Route
              path="*"
              element={
                <Seo
                  title="Montana Community Management | ToolboxLogic"
                  path={window.location.pathname}
                  description="Integrated platform for Montana communities."
                >
                  <Home />
                </Seo>
              }
            />
          </Routes>
        </DemoLayout>
      </BrowserRouter>
    </HelmetProvider>
  );
}

createRoot(document.getElementById('root')!).render(<DemoApp />);
